import { FilterModel } from '../../filter/domain/filterModel';
import { DateTime } from 'luxon';
import { DATE_FORMAT_MMDDYYYYLUXON } from '../../utils';
import { Severity } from '../../graphql/enums';

export enum ActivityOrderBy {
  MostRecentReported = 'MOST_RECENT_REPORTED',
  MostRecentUpdated = 'MOST_RECENT_UPDATED',
  ReminderDate = 'REMINDER_DATE',
  CreationDate = 'CREATION_DATE',
}

export enum ActivityFilterKey {
  Activity = 'ACTIVITY_FILTER',
  AssetActivity = 'ASSET_ACTIVITY_FILTER',
}

export enum DateTypeFilter {
  ALL = 'all',
  TODAY = 'today',
  THISWEEK = 'thisWeek',
  THISMONTH = 'thisMonth',
  CUSTOMDATE = 'customDate',
}

export enum ActivityOptions {
  EVERYTHING = 'EVERYTHING',
  MENTIONED = 'MENTIONED',
  PINNED = 'PINNED',
  SCHEDULED = 'SCHEDULED',
  NOT_SCHEDULED = 'NOT_SCHEDULED',
}

export class ActivityFilterModel implements FilterModel {
  orderBy?: ActivityOrderBy;
  severity?: string[];
  assetName?: string;
  groupIds?: string[];
  labelIds?: string[];
  keyword?: string;
  dateType?: DateTypeFilter;
  startDate?: string;
  endDate?: string;
  isCombinedGroupsFilter?: boolean;
  activityOption?: ActivityOptions;

  constructor(data?: any) {
    // Convert string orderBy back to enum if needed
    if (data?.orderBy && typeof data.orderBy === 'string') {
      // Validate that the string is a valid enum value
      const validOrderByValues = Object.values(ActivityOrderBy);
      if (validOrderByValues.includes(data.orderBy as ActivityOrderBy)) {
        this.orderBy = data.orderBy as ActivityOrderBy;
      } else {
        console.warn('Invalid orderBy value:', data.orderBy, 'falling back to default');
        this.orderBy = ActivityOrderBy.MostRecentUpdated;
      }
    } else {
      this.orderBy = data?.orderBy ?? ActivityOrderBy.MostRecentUpdated;
    }    
    
    this.severity = data?.severity ?? Object.values(Severity);
    this.assetName = data?.assetName;
    this.groupIds = data?.groupIds;
    this.labelIds = data?.labelIds;
    this.activityOption = data?.activityOption ?? ActivityOptions.EVERYTHING;
    this.keyword = data?.keyword;
    this.dateType = data?.dateType ?? DateTypeFilter.ALL;
    this.startDate = data?.startDate ?? '';
    this.endDate = data?.endDate ?? '';
    this.isCombinedGroupsFilter = data?.isCombinedGroupsFilter;
    this.activityOption = data?.activityOption ?? ActivityOptions.EVERYTHING;
  }

  public toJSON(): any {
    return {
      orderBy: this.orderBy,
      severity: this.severity,
      assetName: this.assetName,
      groupIds: this.groupIds,
      labelIds: this.labelIds,
      keyword: this.keyword,
      dateType: this.dateType,
      startDate: this.startDate,
      endDate: this.endDate,
      isCombinedGroupsFilter: this.isCombinedGroupsFilter,
      activityOption: this.activityOption,
    };
  }

  public getFilter(): any {
    const filter: any = {};

    if (this.severity) {
      filter.severity = this.severity;
    }

    if (this.groupIds && this.groupIds.length > 0) {
      filter.groupIds = this.groupIds;
    }

    if (this.labelIds && this.labelIds.length > 0) {
      filter.labelIds = this.labelIds;
    }

    if (this.isCombinedGroupsFilter) {
      filter.isCombinedGroupsFilter = this.isCombinedGroupsFilter;
    }

    if (this.keyword) {
      filter.keyword = this.keyword;
    }

    if (this.activityOption && this.activityOption !== ActivityOptions.EVERYTHING) {
      filter.activityOption = this.activityOption;
    }

    const dateRange = this.getDateRangeByType();
    if (dateRange?.start && dateRange?.end) {
      filter.startDate = dateRange?.start;
      filter.endDate = dateRange?.end;
    }

    return filter;
  }

  public getTypesCount(): number {
    let count = 0;

    if (this.severity) {
      count += this.severity.length;
    }

    if (this.activityOption && this.activityOption !== ActivityOptions.EVERYTHING) {
      count += 1;
    }

    return count;
  }

  public getGroupsCount(): number {
    let count = 0;

    if (this.groupIds) {
      count += this.groupIds.length;
    }

    return count;
  }

  public getLabelsCount(): number {
    let count = 0;

    if (this.labelIds) {
      count += this.labelIds.length;
    }

    return count;
  }

  public scrubWorkspaceData() {
    this.groupIds = undefined;
    this.isCombinedGroupsFilter = undefined;
  }

  public isDateFilterActive() {
    return this.dateType !== DateTypeFilter.ALL;
  }

  private getDateRangeByType() {
    const now = DateTime.now();

    const dateRanges: Record<
      DateTypeFilter,
      () => { start: DateTime; end: DateTime } | undefined
    > = {
      [DateTypeFilter.TODAY]: () => {
        const start = now.startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.THISWEEK]: () => {
        const start = now.minus({ days: 6 }).startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.THISMONTH]: () => {
        const start = now.minus({ days: 29 }).startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.CUSTOMDATE]: () => {
        if (!this.startDate || !this.endDate) return undefined;

        const start = DateTime.fromFormat(
          this.startDate,
          DATE_FORMAT_MMDDYYYYLUXON
        );
        const end = DateTime.fromFormat(
          this.endDate,
          DATE_FORMAT_MMDDYYYYLUXON
        );

        if (!start.isValid || !end.isValid) {
          console.error('Invalid start or end date');
          return undefined;
        }

        const startUTC = start.startOf('day').toUTC();
        const endUTC = end.endOf('day').toUTC();

        return { start: startUTC, end: endUTC };
      },

      [DateTypeFilter.ALL]: () => undefined,
    };

    return dateRanges[this.dateType ?? DateTypeFilter.ALL]?.();
  }
}
